import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Newspaper, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ShareButtons from '../components/ShareButtons';
import RichContent, { stripHtml } from '../components/RichContent';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/news`)
      .then(r => setNews(r.data.news || []))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Haberler · Mikromobilite Dünyasından"
        description="L6e ve L7e mikromobilite dünyasından güncel haberler, mevzuat değişiklikleri, yeni modeller ve topluluk duyuruları."
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs items={[{ name: 'Haberler' }]} />
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Newspaper className="w-8 h-8 text-emerald-600" />
            Haberler
          </h1>
          <p className="text-slate-500 mt-2">L6e ve L7e mikromobilite dünyasından güncel haberler</p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Yükleniyor...</div>
        ) : news.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
            <Newspaper className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500">Henüz haber yok.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {news.map(n => (
              <article key={n._id} data-testid={`news-item-${n._id}`}
                className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-md transition-all">
                <div className="aspect-[16/9] bg-emerald-50 flex items-center justify-center overflow-hidden">
                  {n.image_url ? (
                    <img
                      src={n.image_url.startsWith('http') ? n.image_url : `${API}/files/${n.image_url}`}
                      alt={n.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Newspaper className="w-16 h-16 text-emerald-200" />
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-1 text-xs text-slate-400 mb-2">
                    <Calendar className="w-3 h-3" />
                    {formatDate(n.created_at)}
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {n.title}
                  </h3>
                  {n.content && (expanded[n._id] ? (
                    <>
                      <RichContent html={n.content} className="text-sm" />
                      <button onClick={() => toggle(n._id)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800" data-testid={`news-collapse-${n._id}`}>
                        Daha az göster <ChevronUp className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-600 line-clamp-3">{stripHtml(n.content)}</p>
                      <button onClick={() => toggle(n._id)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800" data-testid={`news-expand-${n._id}`}>
                        Devamını oku <ChevronDown className="w-3 h-3" />
                      </button>
                    </>
                  ))}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <ShareButtons
                      size="xs"
                      url={`${window.location.origin}/haberler#${n._id}`}
                      title={n.title}
                      text={`${n.title} · MiniOto Haberleri`}
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
